import { useState, useCallback, useRef } from 'react';
import { snapToGrid, snapToEndpoint, findWallAtPoint, closestPointOnSegment } from './floorPlanUtils';

export const TOOLS = {
  SELECT: 'select',
  WALL: 'wall',
  DOOR: 'door',
  WINDOW: 'window',
  FURNITURE: 'furniture',
};

export default function useFloorPlanTools({
  state,
  addWall,
  addOpening,
  setSelectedId,
  updateViewport,
}) {
  const [activeTool, setActiveTool] = useState(TOOLS.SELECT);
  const [drawingWall, setDrawingWall] = useState(null); // { x1, y1 } start point
  const [previewPoint, setPreviewPoint] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef(null);
  const spaceDownRef = useRef(false);

  const getSnappedPoint = useCallback((point) => {
    let snapped = point;
    if (state.settings.snapToGrid) {
      snapped = snapToGrid(point, state.settings.gridSize);
    }
    // Also snap to existing endpoints
    snapped = snapToEndpoint(snapped, state.walls, state.settings.gridSize * 0.5);
    return snapped;
  }, [state.settings.snapToGrid, state.settings.gridSize, state.walls]);

  const handleCanvasMouseDown = useCallback((worldPoint, e) => {
    // Middle mouse or space+left click starts pan
    if (e.evt.button === 1 || (spaceDownRef.current && e.evt.button === 0)) {
      setIsPanning(true);
      panStartRef.current = { x: e.evt.clientX, y: e.evt.clientY };
      return;
    }

    if (e.evt.button !== 0) return;

    const snapped = getSnappedPoint(worldPoint);

    switch (activeTool) {
      case TOOLS.WALL:
        if (drawingWall) {
          // Complete the wall
          if (snapped.x !== drawingWall.x1 || snapped.y !== drawingWall.y1) {
            addWall(drawingWall.x1, drawingWall.y1, snapped.x, snapped.y);
            // Start a new wall from the endpoint for continuous drawing
            setDrawingWall({ x1: snapped.x, y1: snapped.y });
          }
        } else {
          // Start a new wall
          setDrawingWall({ x1: snapped.x, y1: snapped.y });
        }
        break;

      case TOOLS.DOOR:
      case TOOLS.WINDOW: {
        const hit = findWallAtPoint(worldPoint, state.walls);
        if (hit) {
          let position = hit.point.t;
          if (state.settings.snapToGrid) {
            const snappedPt = snapToGrid({ x: hit.point.x, y: hit.point.y }, state.settings.gridSize);
            const projected = closestPointOnSegment(
              snappedPt,
              { x: hit.wall.x1, y: hit.wall.y1 },
              { x: hit.wall.x2, y: hit.wall.y2 }
            );
            position = projected.t;
          }
          addOpening(
            activeTool === TOOLS.DOOR ? 'door' : 'window',
            hit.wall.id,
            position,
            activeTool === TOOLS.DOOR ? 36 : 30
          );
        }
        break;
      }

      case TOOLS.SELECT:
      default:
        // Selection handled by individual element click handlers
        break;
    }
  }, [activeTool, drawingWall, addWall, addOpening, getSnappedPoint, state.walls, state.settings.snapToGrid, state.settings.gridSize]);

  const handleCanvasMouseMove = useCallback((worldPoint, e) => {
    if (isPanning && panStartRef.current) {
      const dx = e.evt.clientX - panStartRef.current.x;
      const dy = e.evt.clientY - panStartRef.current.y;
      panStartRef.current = { x: e.evt.clientX, y: e.evt.clientY };
      updateViewport({
        x: state.viewport.x + dx,
        y: state.viewport.y + dy,
      });
      return;
    }

    if (activeTool === TOOLS.WALL && drawingWall) {
      const snapped = getSnappedPoint(worldPoint);
      setPreviewPoint(snapped);
    }
  }, [activeTool, drawingWall, isPanning, getSnappedPoint, updateViewport, state.viewport]);

  const handleCanvasMouseUp = useCallback(() => {
    if (isPanning) {
      setIsPanning(false);
      panStartRef.current = null;
    }
  }, [isPanning]);

  const handleCanvasDoubleClick = useCallback(() => {
    if (activeTool === TOOLS.WALL && drawingWall) {
      // End wall drawing sequence
      setDrawingWall(null);
      setPreviewPoint(null);
    }
  }, [activeTool, drawingWall]);

  const handleWheel = useCallback((e) => {
    e.evt.preventDefault();
    const scaleBy = 1.1;
    const stage = e.target.getStage();
    const pointer = stage.getPointerPosition();
    const oldZoom = state.viewport.zoom;

    const newZoom = e.evt.deltaY < 0
      ? Math.min(oldZoom * scaleBy, 5)
      : Math.max(oldZoom / scaleBy, 0.1);

    // Zoom toward pointer position
    const mousePointTo = {
      x: (pointer.x - state.viewport.x) / oldZoom,
      y: (pointer.y - state.viewport.y) / oldZoom,
    };

    updateViewport({
      zoom: newZoom,
      x: pointer.x - mousePointTo.x * newZoom,
      y: pointer.y - mousePointTo.y * newZoom,
    });
  }, [state.viewport, updateViewport]);

  const zoomIn = useCallback((centerX, centerY) => {
    const oldZoom = state.viewport.zoom;
    const newZoom = Math.min(oldZoom * 1.25, 5);
    const mousePointTo = {
      x: (centerX - state.viewport.x) / oldZoom,
      y: (centerY - state.viewport.y) / oldZoom,
    };
    updateViewport({
      zoom: newZoom,
      x: centerX - mousePointTo.x * newZoom,
      y: centerY - mousePointTo.y * newZoom,
    });
  }, [state.viewport, updateViewport]);

  const zoomOut = useCallback((centerX, centerY) => {
    const oldZoom = state.viewport.zoom;
    const newZoom = Math.max(oldZoom / 1.25, 0.1);
    const mousePointTo = {
      x: (centerX - state.viewport.x) / oldZoom,
      y: (centerY - state.viewport.y) / oldZoom,
    };
    updateViewport({
      zoom: newZoom,
      x: centerX - mousePointTo.x * newZoom,
      y: centerY - mousePointTo.y * newZoom,
    });
  }, [state.viewport, updateViewport]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === ' ') {
      spaceDownRef.current = true;
    }
    if (e.key === 'Escape') {
      setDrawingWall(null);
      setPreviewPoint(null);
      if (activeTool !== TOOLS.SELECT) {
        setActiveTool(TOOLS.SELECT);
      }
      setSelectedId(null);
    }
  }, [activeTool, setSelectedId]);

  const handleKeyUp = useCallback((e) => {
    if (e.key === ' ') {
      spaceDownRef.current = false;
    }
  }, []);

  const cancelDrawing = useCallback(() => {
    setDrawingWall(null);
    setPreviewPoint(null);
  }, []);

  return {
    activeTool,
    setActiveTool,
    drawingWall,
    previewPoint,
    isPanning,
    handleCanvasMouseDown,
    handleCanvasMouseMove,
    handleCanvasMouseUp,
    handleCanvasDoubleClick,
    handleWheel,
    handleKeyDown,
    handleKeyUp,
    cancelDrawing,
    zoomIn,
    zoomOut,
  };
}
